import { clearExposureCounts } from './exposureCounts';

const VISIT_COOKIE = 'mercury_last_authentication';
const MAX_AGE_SECONDS = 60 * 60 * 24 * 400;

type Visit = {
  userId: string;
  sessionId: string;
  recordedAt: string;
  previousAt: string | null;
};

const parseVisit = (raw: string | null): Visit | null => {
  if (!raw) return null;

  const [userId, sessionId, recordedAt, previousAt = ''] = raw.split('~');
  if (!userId || !sessionId || !recordedAt) return null;
  if (Number.isNaN(new Date(recordedAt).getTime())) return null;

  return {
    userId,
    sessionId,
    recordedAt,
    previousAt: previousAt || null,
  };
};

const serializeVisit = (visit: Visit) =>
  [visit.userId, visit.sessionId, visit.recordedAt, visit.previousAt ?? ''].join('~');

const sessionIdFromPart = (part: string): string | null => {
  const trimmed = part.trim();
  const separator = trimmed.indexOf('=');
  if (separator < 0) return null;

  const name = trimmed.slice(0, separator);
  const value = decodeURIComponent(trimmed.slice(separator + 1));
  return name.startsWith('session_id') && value ? value : null;
};

/** Session cookies are named session_id, session_id_dev, and the same pattern per environment. */
export const readSessionId = (cookieHeader: string): string | null =>
  cookieHeader
    .split(';')
    .map(sessionIdFromPart)
    .find((value) => value !== null) ?? null;

export const readVisitCookie = (): string | null => {
  const prefix = `${VISIT_COOKIE}=`;
  const found = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return found ? decodeURIComponent(found.slice(prefix.length)) : null;
};

const cookieSuffix = () => (window.location.protocol === 'https:' ? '; Secure' : '');

export const writeVisitCookie = (value: string) => {
  document.cookie = `${VISIT_COOKIE}=${encodeURIComponent(value)}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${cookieSuffix()}`;
};

export const clearVisitCookie = () => {
  document.cookie = `${VISIT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${cookieSuffix()}`;
};

/**
 * Login time stored for this user. Reading never writes the cookie.
 * The time kept from the login before this one is the window start.
 * The first login for this user has none, so the caller uses 15 days.
 * No cookie means the same.
 */
export const readPreviousAuthentication = (
  userId: string,
  read: () => string | null
): string | null => {
  const stored = parseVisit(read());
  if (stored?.userId !== userId) return null;

  return stored.previousAt;
};

const JUST_WRITTEN_MS = 2000;

/**
 * Stores this login and keeps the one before it. A repeated call for the same
 * session does nothing, so navigation cannot move the saved time. A real login
 * deletes the cookie and creates it again, even when the session id is unchanged.
 */
export const recordNewLogin = ({
  userId,
  sessionId,
  now,
  read,
  write,
  clear,
  force = false,
}: {
  userId: string;
  sessionId: string;
  now: Date;
  read: () => string | null;
  write: (value: string) => void;
  clear: () => void;
  force?: boolean;
}): void => {
  if (!userId || !sessionId) return;

  const stored = parseVisit(read());
  const sameSession = stored?.userId === userId && stored.sessionId === sessionId;
  const age = stored ? now.getTime() - new Date(stored.recordedAt).getTime() : JUST_WRITTEN_MS;
  if (!force && sameSession) return;
  if (force && sameSession && age < JUST_WRITTEN_MS) return;

  const previousAt = stored?.userId === userId ? stored.recordedAt : null;
  clear();
  clearExposureCounts();
  write(
    serializeVisit({
      userId,
      sessionId,
      recordedAt: now.toISOString(),
      previousAt,
    })
  );
};
